/**
 * TASK HELPERS - UTILITY FUNCTIONS FOR TASK OPERATIONS
 *
 * This file contains pure utility functions for task manipulation:
 * - Sorting algorithms for tasks
 * - Data formatting functions
 * - Position rebalancing utilities
 *
 * These functions are stateless and can be tested independently.
 */

import { TaskData } from '../taskTypes';
import { db } from '../../../lib/offline-db';
import { logger } from '../../../utils/logger';

/**
 * Helper function to sort tasks consistently by status and position
 * Used for maintaining consistent task order across the application
 */
export const sortTasks = (tasks: TaskData[]): TaskData[] => {
  return [...tasks].sort((a, b) => {
    // First sort by status
    if (a.status !== b.status) {
      const statusOrder = { todo: 0, doing: 1, done: 2 };
      const statusA = statusOrder[a.status as keyof typeof statusOrder] ?? 99;
      const statusB = statusOrder[b.status as keyof typeof statusOrder] ?? 99;
      return statusA - statusB;
    }
    // Then by position within same status
    if (a.position !== b.position) {
      return (a.position || 0) - (b.position || 0);
    }
    // If positions are the same, sort by created_at in descending order (newer first)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
};

/**
 * Helper function for deadline-based sorting
 * Used specifically for deadline-sorted task boards
 */
export const sortByDeadline = (tasks: TaskData[]): TaskData[] => {
  return [...tasks].sort((a, b) => {
    // Tasks without deadlines go at the end
    if (!a.deadline && !b.deadline) return 0;
    if (!a.deadline) return 1;
    if (!b.deadline) return -1;

    // Validate deadline format and sort by date
    try {
      const dateA = new Date(a.deadline);
      const dateB = new Date(b.deadline);

      // Additional validation to catch invalid dates
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        // Fall back to string comparison in case of invalid dates
        return String(a.deadline).localeCompare(String(b.deadline));
      }

      return dateA.getTime() - dateB.getTime();
    } catch {
      // Fall back to position-based sorting in case of errors
      return (a.position || 0) - (b.position || 0);
    }
  });
};

/**
 * Helper function to rebalance task positions locally (for offline mode)
 * Ensures consistent spacing between task positions to prevent conflicts
 */
export async function rebalanceTaskPositionsLocally(
  topicId: string,
  status: string
): Promise<boolean> {
  try {
    // Get all tasks with this status
    const tasks = await db.tasks
      .where('topic_id')
      .equals(topicId)
      .and(task => task.status === status && !task.isDeleted)
      .toArray();

    // Sort by current position
    tasks.sort((a, b) => (a.position || 0) - (b.position || 0));

    // Rebalance positions with 1000-unit spacing
    const updatedTasks = tasks.map((task, index) => ({
      ...task,
      position: (index + 1) * 1000,
      updated_at: new Date().toISOString(),
    }));

    // Update all tasks in IndexedDB
    await db.tasks.bulkPut(updatedTasks);

    logger.store(`Rebalanced ${updatedTasks.length} tasks in status ${status} (offline mode)`);

    return true;
  } catch (error) {
    logger.error('Error rebalancing task positions locally:', error);
    return false;
  }
}
